import type { ConversationPublic } from '@repo/types/chat';
import type { MessageReadReceipts } from '@repo/types/message';
import type { InfiniteData } from '@tanstack/react-query';

import { MessagePublic } from '@repo/types/message';
import { UserPublic } from '@repo/types/users';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { ChatKey } from './keys';

export function useOptimisticConversationReadState(chatId: number | null, user: UserPublic) {
  const queryClient = useQueryClient();
  const update = useCallback(
    (messageId: number) => {
      if (chatId == null || user.id < 0) return;

      queryClient.setQueriesData<InfiniteData<ConversationPublic[]>>(
        {
          queryKey: [ChatKey.CHAT_LAST_CONVERSATIONS_INFINITE],
          exact: false,
        },
        (current) => {
          if (!current) return current;

          let mutated: boolean = false;
          const nextPages: ConversationPublic[][] = current.pages.map((page) =>
            page.map((conversation) => {
              if (conversation.id !== chatId) {
                return conversation;
              }

              const lastMessage: MessagePublic | null = conversation.message;
              if (!lastMessage || lastMessage.id !== messageId) {
                return conversation;
              }

              const alreadyRead: boolean | undefined = lastMessage.readReceipts?.some((receipt) => receipt.id === user.id);
              if (alreadyRead) {
                return conversation;
              }

              mutated = true;
              const newReceipt: MessageReadReceipts = {
                ...user,
                messagereadreceipts: {
                  createdAt: new Date().toISOString(),
                },
              };

              return {
                ...conversation,
                message: {
                  ...lastMessage,
                  readReceipts: [...(lastMessage.readReceipts ?? []), newReceipt],
                },
              };
            })
          );

          if (!mutated) {
            return current;
          }

          return {
            ...current,
            pages: nextPages,
          };
        }
      );
    },
    [chatId, user, queryClient]
  );

  return update;
}
